/**
 * RelarkComplianceSection
 * -----------------------
 * Samlet compliance- og sikkerhedssektion med to tabs:
 *  - "Sikkerhed & data" → TrustLifecycleStrip (DPO/IT-vinkel)
 *  - "AI Act-native"    → RelarkArticle14Native (AI-governance-vinkel)
 *
 * Erstatter de to tidligere ryg-mod-ryg sektioner (SecuritySection +
 * RelarkArticle14Native), så vi ikke trætter læseren med to compliance-mure
 * i træk. DPO-speed-linjen står øverst som fælles trust-anker.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Gavel } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import TrustLifecycleStrip from "@/components/landing/TrustLifecycleStrip";
import RelarkArticle14Native from "./RelarkArticle14Native";

type Tab = "security" | "ai-act";

export default function RelarkComplianceSection() {
  const { t } = useTranslation("ui");
  const [tab, setTab] = useState<Tab>("security");

  const tabs: { key: Tab; icon: typeof Shield; label: string }[] = [
    { key: "security", icon: Shield, label: t("relark.complianceTabs.security") },
    { key: "ai-act", icon: Gavel, label: t("relark.complianceTabs.aiAct") },
  ];

  return (
    <section id="security" className="bg-accent/20 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5">
        {/* Fælles header / DPO-speed anker */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
            {t("relark.complianceTabs.eyebrow")}
          </p>
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t("relark.complianceTabs.title")}
          </h2>
          <p className="mt-3 text-sm font-medium text-foreground">
            {t("relark.compliance.dpoSpeed")}
          </p>
        </div>

        {/* Tab switcher */}
        <div
          role="tablist"
          aria-label={t("relark.complianceTabs.title")}
          className="mx-auto mt-8 inline-flex w-full max-w-md items-center rounded-full border border-border bg-card p-1 shadow-sm sm:flex"
        >
          {tabs.map(({ key, icon: Icon, label }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                aria-controls={`compliance-panel-${key}`}
                id={`compliance-tab-${key}`}
                type="button"
                onClick={() => {
                  setTab(key);
                  trackEvent("compliance_tab_click", { tab: key });
                }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition ${
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Centered wrapper for switcher on mobile */}
        <div className="mt-8">
          <div
            role="tabpanel"
            id="compliance-panel-security"
            aria-labelledby="compliance-tab-security"
            hidden={tab !== "security"}
          >
            {tab === "security" && <TrustLifecycleStrip />}
          </div>
          <div
            role="tabpanel"
            id="compliance-panel-ai-act"
            aria-labelledby="compliance-tab-ai-act"
            hidden={tab !== "ai-act"}
          >
            {tab === "ai-act" && <RelarkArticle14Native embedded />}
          </div>
        </div>
      </div>
    </section>
  );
}
